import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { createQueryGroup } from "../../core/api/queries";
import { BodyOf } from "../../core/api";
import {
  PatientId,
  VisitInstanceDailyNoteAttestationId,
  VisitInstanceDailyNoteId,
} from "../../shared/schema/schema";

export type DashboardFilters = BodyOf<"post", "./visit_daily_notes/dashboard">;

export default createQueryGroup(({ api }) => ({
  /**
   * The Dashboard tab: the backlog (always current) and the statistics for a window of visits.
   *
   * Under the `visitDailyNotes` key so signing a batch, or sending a failed sync again, refreshes
   * the numbers along with the tables. The previous figures stay on screen while a changed
   * filter loads, so the charts do not flash empty.
   */
  dashboard: (filters: DashboardFilters) =>
    queryOptions({
      queryKey: ["visitDailyNotes", "dashboard", filters],
      queryFn: () => api.post("./visit_daily_notes/dashboard", { body: filters }),
      placeholderData: keepPreviousData,
    }),

  /**
   * The offices this agency has switched the workflow on for.
   *
   * Also what decides whether the page is offered at all: an empty list means the
   * agency is not part of the rollout.
   */
  reviewOffices: () =>
    queryOptions({
      queryKey: ["visitDailyNotes", "offices"],
      queryFn: () => api.get("./visit_daily_notes/offices", {}),
      select: ({ offices }) => offices,
    }),

  // The wording comes from the server so what is shown and what is recorded
  // cannot drift apart, and so the version can be sent back with the signature.
  attestationDeclaration: () =>
    queryOptions({
      queryKey: ["visitDailyNotes", "attestationDeclaration"],
      queryFn: () => api.get("./visit_daily_notes/attestation_declaration", {}),
    }),

  // One signed batch with the manifest the co-signer reads before signing.
  attestationById: (visitInstanceDailyNoteAttestationId: VisitInstanceDailyNoteAttestationId) =>
    queryOptions({
      queryKey: ["visitDailyNoteAttestation", visitInstanceDailyNoteAttestationId],
      queryFn: () =>
        api.get("./visit_daily_note_attestations/:visitInstanceDailyNoteAttestationId", {
          path: { visitInstanceDailyNoteAttestationId },
        }),
    }),

  /**
   * One patient's daily notes at a glance: the header of the patient view.
   *
   * Under the `visitDailyNotes` key on purpose, so the invalidation a signature triggers
   * refreshes the counts along with the tables.
   */
  patientSummary: (patientId: PatientId) =>
    queryOptions({
      queryKey: ["visitDailyNotes", "patientSummary", patientId],
      queryFn: () =>
        api.get("./visit_daily_notes/patients/:patientId/summary", {
          path: { patientId },
        }),
    }),

  byId: (visitInstanceDailyNoteId: VisitInstanceDailyNoteId) =>
    queryOptions({
      queryKey: ["visitDailyNote", visitInstanceDailyNoteId],
      queryFn: () =>
        api.get("./visit_daily_notes/:visitInstanceDailyNoteId", {
          path: { visitInstanceDailyNoteId },
        }),
    }),

  /**
   * Which of these notes the signed-in member may not sign, and the license state each needs.
   *
   * The same rule the server applies when signing, asked before so the attestation can say
   * what is missing instead of failing at the signature. A POST because the note list is a
   * body; it reads nothing and changes nothing.
   */
  signOffCheck: (visitInstanceDailyNoteIds: VisitInstanceDailyNoteId[]) =>
    queryOptions({
      queryKey: ["visitDailyNotes", "signOffCheck", visitInstanceDailyNoteIds],
      queryFn: () =>
        api.post("./visit_daily_notes/sign_off_check", { body: { visitInstanceDailyNoteIds } }),
      select: ({ blocked }) => blocked,
    }),

  // The agency's nurses' licenses, which decide who may sign daily notes.
  licenses: () =>
    queryOptions({
      queryKey: ["agencyMemberLicenses"],
      queryFn: () => api.get("./agency_member_licenses", {}),
      select: ({ licenses }) => licenses,
    }),
}));
